import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { WorkAuthorUpdateStore } from "../../store";
import { runInAction } from "mobx";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { AuthorComposerCreate } from "../../../authorComposer/components/create/AuthorComposerCreate";
import { AuthorComposerCreateStore } from "../../../authorComposer/store";
import { InputNumber } from "../../../common/controls";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { WorkAuthorPublisherUpdate } from "../../../workAuthorPublisher/components/update/WorkAuthorPublisherUpdate";
import { WorkAuthorPublisherUpdateStore } from "../../../workAuthorPublisher/store";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type WorkAuthorUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  authorSelectedValue?: { displayId: string; id: string } | null;
  onAuthorChange?: (value: string) => void;
  onAuthorSelected?: (value: { displayId: string; id: string } | null) => void;
  authorOptions?: { displayId: string; id: string }[];
  authorError?: string;
  viewFormAuthor?: () => void;
  lyricsPercentage: number;
  lyricsPercentageSelectedValue?: { displayId: string; id: string } | null;
  onLyricsPercentageChange?: (value: number) => void;
  lyricsPercentageError?: string;
  musicPercentage: number;
  musicPercentageSelectedValue?: { displayId: string; id: string } | null;
  onMusicPercentageChange?: (value: number) => void;
  musicPercentageError?: string;
  publishersOnRestore: (id: string) => void;
  publishersOnCommitChanges: (changes: GridChangeSet) => void;
  publishersOnCommitInlineChange: (
    rowId: string,
    changes: Record<string, any>
  ) => void;
  publishersValidateChanges: (changes: GridChangeSet) => boolean;
  publishersRows: CrudRow[];
  publishersError?: string;
  workAuthorPublisherUpdateStore: WorkAuthorPublisherUpdateStore;
  publishersVisibleColumns: GridColumn[];
  publishersColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
    }
  >;
  dummyCRUDRestoredObservable: number;
  workAuthorUpdateStore: WorkAuthorUpdateStore;
};

export const WorkAuthorUpdateForm: React.FC<WorkAuthorUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onAuthorSelected = () => {},
      authorOptions = [],
      authorSelectedValue = null,
      authorError,
      onAuthorChange = () => {},
      viewFormAuthor = () => {},
      lyricsPercentage = 0,
      lyricsPercentageError,
      onLyricsPercentageChange = () => {},
      musicPercentage = 0,
      musicPercentageError,
      onMusicPercentageChange = () => {},
      publishersOnRestore,
      publishersOnCommitChanges,
      publishersOnCommitInlineChange,
      publishersValidateChanges,
      publishersRows,
      publishersError,
      workAuthorPublisherUpdateStore,
      publishersVisibleColumns,
      publishersColumnTypes,
      dummyCRUDRestoredObservable,
      workAuthorUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      if (workAuthorPublisherUpdateStore.status === "uninitialized") {
        workAuthorPublisherUpdateStore.initialize();
      }

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="WorkAuthor"
              mode="update"
              values={{}}
              options={{ author: authorOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "author":
                    onAuthorSelected?.(valor);
                    break;
                  case "lyricsPercentage":
                    onLyricsPercentageChange?.(valor);
                    break;
                  case "musicPercentage":
                    onMusicPercentageChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={workAuthorUpdateStore.getCardErrors("null")}
              title={langStore.groupWorkAuthorLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="author"
                  label={langStore.WorkAuthorAuthorLabel}
                  autoFocus
                  error={Boolean(authorError)}
                  helperText={
                    authorError ||
                    workAuthorUpdateStore.warnings["author"]?.message
                  }
                  selectedValue={authorSelectedValue}
                  onChange={(value: string) => onAuthorChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onAuthorSelected(value)
                  }
                  options={authorOptions}
                  description={langStore.WorkAuthorAuthorDescription}
                  onView={viewFormAuthor}
                  canCreate={rootStore.authStore.hasPermissions(
                    "authorComposer_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new AuthorComposerCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <AuthorComposerCreate
                          authorComposerCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewAuthorComposer"
                      cancelMethod="cancelNewAuthorComposer"
                    />
                  )}
                />
                <InputNumber
                  value={lyricsPercentage}
                  error={Boolean(lyricsPercentageError)}
                  warning={
                    !lyricsPercentageError &&
                    Boolean(workAuthorUpdateStore.warnings["lyricsPercentage"])
                  }
                  helperText={
                    lyricsPercentageError ||
                    workAuthorUpdateStore.warnings["lyricsPercentage"]?.message
                  }
                  label={langStore.WorkAuthorLyricsPercentageLabel}
                  decimalScale={2}
                  description={langStore.WorkAuthorLyricsPercentageDescription}
                  onChange={(value: number) => onLyricsPercentageChange(value)}
                />
                <InputNumber
                  value={musicPercentage}
                  error={Boolean(musicPercentageError)}
                  warning={
                    !musicPercentageError &&
                    Boolean(workAuthorUpdateStore.warnings["musicPercentage"])
                  }
                  helperText={
                    musicPercentageError ||
                    workAuthorUpdateStore.warnings["musicPercentage"]?.message
                  }
                  label={langStore.WorkAuthorMusicPercentageLabel}
                  decimalScale={2}
                  description={langStore.WorkAuthorMusicPercentageDescription}
                  onChange={(value: number) => onMusicPercentageChange(value)}
                />
                <ItemsCRUDCompact
                  label={langStore.WorkAuthorPublishersLabel}
                  description={langStore.WorkAuthorPublishersDescription}
                  showLabel={true}
                  rows={publishersRows}
                  columns={publishersVisibleColumns}
                  columnTypes={publishersColumnTypes}
                  canCreate={true}
                  isEditVisible={() =>
                    rootStore.authStore.hasPermissions(
                      "workAuthorPublisher_update"
                    )
                  }
                  isDeleteVisible={() =>
                    rootStore.authStore.hasPermissions(
                      "workAuthorPublisher_delete"
                    )
                  }
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() =>
                        workAuthorUpdateStore.publishersLoadRow(row)
                      );
                    }
                    return (
                      <WorkAuthorPublisherUpdate
                        workAuthorPublisherUpdateStore={
                          workAuthorPublisherUpdateStore
                        }
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="sm"
                  onRestore={(id: string) => publishersOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    publishersOnCommitChanges(changes)
                  }
                  onCommitInlineChange={(
                    rowId: string,
                    changes: Record<string, any>
                  ) => publishersOnCommitInlineChange(rowId, changes)}
                  validate={(changes: GridChangeSet) =>
                    publishersValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={publishersError}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
